import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { LayoutDashboard, Armchair, Home as HomeIcon, FolderOpen, Settings, LogOut, Globe } from "lucide-react";

export default function AdminLayout() {
  const { user, loading, logout } = useAdminAuth();
  const navigate = useNavigate();

  // Authentication gate
  React.useEffect(() => {
    if (loading) return;
    if (!user) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stoel-paper text-stoel-inkSoft">
        <div className="animate-pulse text-sm tracking-wide">Loading…</div>
      </div>
    );
  }

  if (!user) return null;

  const items = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
    { to: "/admin/classics", label: "Klassiekers", icon: Armchair, testid: "admin-nav-classics" },
    { to: "/admin/rooms", label: "Ruimtes", icon: HomeIcon, testid: "admin-nav-rooms" },
    { to: "/admin/projects", label: "Projecten", icon: FolderOpen, testid: "admin-nav-projects" },
    { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
  ];

  return (
    <div className="min-h-screen flex bg-stoel-paperDeep/40">
      <aside data-testid="admin-sidebar" className="w-60 shrink-0 border-r border-stoel-line bg-stoel-paper flex flex-col">
        <div className="px-6 py-7 border-b border-stoel-line">
          <div className="text-xs uppercase tracking-[0.3em] text-stoel-walnut">Admin</div>
          <div className="mt-1 text-xl font-light tracking-tight text-stoel-ink">DeStoel.com</div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-stoel-ink text-stoel-paper"
                    : "text-stoel-ink hover:bg-stoel-paperDeep"
                }`
              }
            >
              <it.icon size={16} />
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-stoel-line px-3 py-4 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-view-site"
            className="flex items-center gap-3 px-3 py-2 text-xs text-stoel-walnut hover:text-stoel-terracotta"
          >
            <Globe size={14} /> Bekijk publieke site
          </a>
          <div className="px-3 py-2 text-xs text-stoel-inkSoft truncate" data-testid="admin-user-email">
            {user.email}
          </div>
          <button
            data-testid="admin-logout"
            onClick={async () => {
              await logout();
              navigate("/admin/login", { replace: true });
            }}
            className="w-full flex items-center gap-3 px-3 py-2 text-xs text-stoel-ink hover:bg-stoel-paperDeep"
          >
            <LogOut size={14} /> Uitloggen
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 md:p-12 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
